import { TableColumns } from "@flaminem/ui-components";
import { isDefined } from "@flaminem/utils";
import { useCallback } from "react";

import MatchElementIcon from "src/dynamic-view-renderer/Widgets/RelatedCases/MatchModal/MatchElementIcon.tsx";
import {
  Comparison,
  RelatedCasesDisplayElementConfig,
  RelatedCasesMatchType,
  RelatedWidgetConfiguration,
} from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import { useFormatPropertyByCode } from "src/hooks/property/useFormatProperyByCode";
import { useFormatTypedValue } from "src/hooks/useFormatTypedValue";
import { useT } from "src/internationalization";
import { Case } from "src/types/case";
import { Element } from "src/types/element";
import { PropertyValue } from "src/types/property";

// remove diacritics
export const normalizeString = (str: string) => {
  return str
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

// case insensitive, ignore empty values, trim and normalize
const isValueEqual = (
  str1?: string,
  str2?: string,
  option?: {
    ignoreEmpty?: boolean;
  }
): boolean => {
  const str1Normalized = normalizeString(str1 ?? "");
  const str2Normalized = normalizeString(str2 ?? "");
  if (option?.ignoreEmpty) {
    if (str1Normalized === "" || str2Normalized === "") {
      return true;
    }
  }
  return str1Normalized === str2Normalized;
};

export const getConfiguredElements = (
  configuration: RelatedWidgetConfiguration,
  elements?: Element[]
): Element[] => {
  return (
    elements?.filter((elem) =>
      configuration["matchElementsColumns"]
        .map((elem) => elem.code)
        .includes(elem.schemaElement.code)
    ) ?? []
  );
};

const compareElements = (
  elem: Element | null | undefined,
  other: Element | null | undefined
): Comparison => {
  // identifier values that are equal
  // TODO: we can later filter `elem.identifiers` based on a configuration
  const equalIdentifiers =
    elem?.identifiers?.filter(({ value, identifierType }) => {
      const otherValue = other?.identifiers?.find((elem) => {
        return elem.identifierType.code === identifierType.code;
      })?.value;

      return isValueEqual(value, otherValue, { ignoreEmpty: elem?.name === other?.name });
    }) ?? [];

  let match: RelatedCasesMatchType;
  const size = equalIdentifiers.length;
  if (size === 0) {
    match = "None";
  } else if (elem?.schemaElement.code !== other?.schemaElement.code) {
    match = "Cross";
  } else if (size === elem?.identifiers.length) {
    match = "Full";
  } else {
    match = "Partial";
  }

  const identifiers =
    elem?.identifiers?.map((identifier) => {
      const otherIdentifier = other?.identifiers.find(
        (elem) => elem.identifierType.code === identifier.identifierType.code
      );
      const isCrossComparison = elem?.schemaElement.code !== other?.schemaElement.code;

      return {
        code: identifier.schemaIdentifier.code,
        name: identifier.schemaIdentifier.name,
        match: isValueEqual(identifier.value, otherIdentifier?.value, {
          ignoreEmpty: !isCrossComparison,
        }),
        values: [identifier.value, otherIdentifier?.value],
      };
    }) ?? [];

  return {
    match,
    elements: {
      current: elem?.name ?? "-",
      compared: other?.name ?? "-",
    },
    identifiers,
  };
};

// compares currentRowCase element in column `column` with all configuredElements
// if a cross-match exists, returns the cross-math otherwise returns the match with the elment that has the same type
export const getComparison = (
  currentRowCase: Case | null,
  column: RelatedCasesDisplayElementConfig,
  alertElements: Element[]
): Comparison | undefined => {
  const currentRowElement = currentRowCase?.elements.find(
    (elem) => elem.schemaElement.code === column.code
  );

  const comparisons = alertElements.map((alertElem) => {
    return compareElements(alertElem, currentRowElement);
  });

  const crossMatch = comparisons?.find((x) => x.match === "Cross");
  const currentMatch = comparisons?.find((x) => x.match !== "None");

  return crossMatch ?? currentMatch;
};

export type MappedCaseObject = {
  id: string;
  typeCode: string;
  [key: string]: string | Case | null;
};

export const useMapCaseObject = () => {
  const formatTypedValue = useFormatTypedValue();

  return useCallback(
    (caseData: Case, configuration: RelatedWidgetConfiguration): MappedCaseObject | null => {
      if (!caseData) {
        return null;
      }

      const caseCols = configuration.caseColumns;
      const mappedObject: MappedCaseObject = { id: caseData.id, typeCode: caseData.typeCode };

      caseCols.forEach((column) => {
        const element: { properties: PropertyValue[] } | null = column.value.elementPath
          ? (caseData.elements.find(
              ({ schemaElement }) => schemaElement.code === column.value.elementPath
            ) ?? null)
          : caseData;

        mappedObject[column._label] =
          element?.properties
            .reduce<string[]>((values, elementProp) => {
              if (column.value.properties?.includes(elementProp.property.code)) {
                const formattedValue = formatTypedValue(elementProp.value);
                return [...values, formattedValue];
              }
              return values;
            }, [])
            .join(column.value.separator || " ") || "";
      });

      mappedObject["row"] = caseData;
      return mappedObject;
    },
    [formatTypedValue]
  );
};

export const useGetRelatedCasesColumns = () => {
  const t = useT();
  const formatPropertyByCode = useFormatPropertyByCode();
  return (
    configuration: RelatedWidgetConfiguration,
    propertyValues?: PropertyValue[],
    elements?: Element[]
  ): TableColumns<MappedCaseObject | undefined | null> => {
    console.assert(!!configuration, "RelatedWidget configuration is undefined");
    const caseCols = configuration.caseColumns.map((column) => [
      column?.code ? column.code : (column._label ?? column.label),
      {
        flexGrow: column.flexGrow || (column.width ? 0 : 1),
        width: column.width ?? 1,
        label: column.label,
        render: (row: MappedCaseObject) => row[column._label ?? column.label],
      },
    ]);

    const matchCols = (configuration.matchElementsColumns ?? [])
      .map((column) => {
        return {
          flexGrow: column.width ? 0 : 1,
          width: column.width ?? 1,
          label: column.label,
          _label: column._label,
          render: (mappedValue: MappedCaseObject) => {
            const caseData = mappedValue.row as Case;
            const comparison = getComparison(
              caseData,
              column,
              getConfiguredElements(configuration, elements)
            );

            const alertCaseId = formatPropertyByCode(propertyValues ?? [], "id") ?? "-";
            const currenCaseId = formatPropertyByCode(caseData.properties, "id") ?? "-";

            return (
              //eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <div onClick={(event) => event.stopPropagation()}>
                <MatchElementIcon
                  comparison={comparison}
                  title={t("component.widget.related.cases.matched.icon.title", {
                    alertCaseId,
                    currenCaseId,
                  })}
                />
              </div>
            );
          },
        };
      })
      .map((col) => [col._label ?? col.label, col]);

    return Object.fromEntries([...caseCols, ...matchCols]);
  };
};

export const useBuildCaseRows = () => {
  const mapCaseObject = useMapCaseObject();

  return useCallback(
    (cases: Case[], configuration: RelatedWidgetConfiguration): MappedCaseObject[] => {
      return cases.map((caseData) => mapCaseObject(caseData, configuration)).filter(isDefined);
    },
    [mapCaseObject]
  );
};
